import { useRef, useState } from "react";
import { Button, Menu } from "@jugl-web/ui-kit";
import { cx, useTranslations } from "@jugl-web/utils";
import { useClipboard } from "@jugl-web/utils/hooks/useClipboard";
import {
  InvoicePreview,
  InvoicePreviewHandle,
} from "@jugl-web/domain-resources/invoices/components/InvoicePreview";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import useEntity from "@web-src/modules/entities/hooks/useEntity";
import { useInvoicesItemFormPageContext } from "@web-src/modules/invoices/pages/InvoicesItemFormPage/context/useInvoicesItemFormPageContext";
import { useInvoicesConfig } from "@web-src/modules/invoices/pages/InvoicesSettingsPage/pages/InvoicesBusinessInformationPage/hooks/useInvoicesConfig";
import { ReactComponent as ChevronIcon } from "./assets/chevron.svg";
import { ReactComponent as DownloadIcon } from "./assets/download.svg";
import { ReactComponent as LinkIcon } from "./assets/link.svg";

interface ComponentProps {
  className?: string;
}

export const InvoiceItemFormButtons = ({ className = "" }: ComponentProps) => {
  const { t } = useTranslations();
  const { submitForm, invoiceItemData, isSubmitting } =
    useInvoicesItemFormPageContext();
  const invoicePreviewRef = useRef<InvoicePreviewHandle>(null);
  const { copyToClipboard } = useClipboard();
  const { navigateToPage } = useNavigation();
  const { entity } = useEntity();
  const [currentInvoiceNo, setCurrentInvoiceNo] = useState<string | undefined>(
    invoiceItemData.no
  );
  const { config } = useInvoicesConfig();

  const menuSections = [
    [
      {
        id: "save-download",
        label: t({
          id: "invoices.save-and-download-pdf",
          defaultMessage: "Save and Download PDF",
        }),
        icon: <DownloadIcon />,
      },
      {
        id: "save-copy-url",
        label: t({
          id: "invoices.save-and-copy-url",
          defaultMessage: "Save and Copy URL",
        }),
        icon: <LinkIcon />,
      },
    ],
  ];

  const handleMenuSelect = async (item: { id: string }) => {
    switch (item.id) {
      case "save-download": {
        const invoice = await submitForm();
        if (invoice) {
          setCurrentInvoiceNo(invoice.no);
          setTimeout(() => {
            invoicePreviewRef.current?.download();
            navigateToPage("invoices");
          }, 0);
        }
        break;
      }
      case "save-copy-url": {
        const invoice = await submitForm();
        if (invoice) {
          copyToClipboard(invoice.short_url);
          navigateToPage("invoices");
        }
        break;
      }
      case "save-send":
        // TODO: Handle save and send to client
        break;
      default:
        submitForm();
        break;
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) {
      return;
    }
    const response = await submitForm();
    if (!response) {
      return;
    }
    setCurrentInvoiceNo(response.no);
    navigateToPage("invoices");
  };

  return (
    <>
      <div
        className={cx(
          "flex shrink-0 items-center gap-4 border-0 border-t-2 border-solid border-[#EEF2F5] px-[70px] py-6",
          className
        )}
      >
        <Menu
          placement="bottom-start"
          sections={menuSections}
          onSelect={handleMenuSelect}
          renderTrigger={({ Trigger, triggerRef }) => (
            <Trigger
              ref={triggerRef}
              as={Button}
              className={cx("relative h-10 w-[160px] pr-12")}
              isDisabled={isSubmitting}
              onClick={(e) => {
                if (e.target instanceof HTMLButtonElement) {
                  handleSubmit();
                  e.preventDefault();
                }
              }}
              iconEnd={
                <div className="absolute top-0 right-0 flex h-full items-center justify-center">
                  <div
                    className={cx(
                      "h-2/3 w-10 items-center justify-center border-0 border-l border-solid border-white pr-0.5 text-white"
                    )}
                  >
                    <ChevronIcon />
                  </div>
                </div>
              }
            >
              {t({
                id: "common.save",
                defaultMessage: "Save",
              })}
            </Trigger>
          )}
        />
      </div>

      <InvoicePreview
        hidden
        entityName={entity?.name}
        invoice={{ ...invoiceItemData, no: currentInvoiceNo }}
        ref={invoicePreviewRef}
        logo={config?.logo || ""}
      />
    </>
  );
};
